import type { AuditDiffEntry } from '../types/auditLog';

interface JsonDiffViewerProps {
  diff: AuditDiffEntry[];
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '(없음)';
  if (typeof v === 'string') return v;
  return JSON.stringify(v, null, 2);
}

/**
 * JsonDiffViewer — before/after JSON diff 시각화 (read-only)
 * 변경된 필드는 syntax-highlight + 추가(+)/삭제(-) 라인 표시
 */
export function JsonDiffViewer({ diff, before, after }: JsonDiffViewerProps) {
  const added = diff.filter((d) => d.before === null && d.after !== null).length;
  const removed = diff.filter((d) => d.before !== null && d.after === null).length;
  const changed = diff.filter((d) => d.before !== null && d.after !== null).length;

  return (
    <div>
      {/* Diff 요약 */}
      {diff.length > 0 && (
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-3)',
            flexWrap: 'wrap',
            marginBottom: 'var(--spacing-3)',
            fontSize: 'var(--font-size-xs)',
          }}
        >
          {added > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontWeight: 600,
                background: 'color-mix(in srgb, var(--color-success) 14%, transparent)',
                color: 'var(--color-success)',
                border: '1px solid var(--color-success)',
              }}
            >
              +{added} 추가
            </span>
          )}
          {removed > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontWeight: 600,
                background: 'color-mix(in srgb, var(--color-danger) 14%, transparent)',
                color: 'var(--color-danger)',
                border: '1px solid var(--color-danger)',
              }}
            >
              -{removed} 제거
            </span>
          )}
          {changed > 0 && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--spacing-1)',
                padding: '2px var(--spacing-2)',
                borderRadius: 'var(--radius-sm)',
                fontFamily: 'var(--font-family-mono)',
                fontWeight: 600,
                background: 'color-mix(in srgb, var(--color-warning) 14%, transparent)',
                color: 'var(--color-warning)',
                border: '1px solid var(--color-warning)',
              }}
            >
              ~{changed} 변경
            </span>
          )}
        </div>
      )}

      {/* Diff 테이블 */}
      {diff.length > 0 ? (
        <table
          aria-label="변경 내역"
          style={{
            width: '100%',
            borderCollapse: 'collapse',
            fontFamily: 'var(--font-family-mono)',
            fontSize: 'var(--font-size-xs)',
            background: 'var(--color-surface-elevated)',
            border: '1px solid var(--color-surface-border)',
            borderRadius: 'var(--radius-sm)',
            overflow: 'hidden',
          }}
        >
          <thead>
            <tr style={{ background: 'var(--color-surface-muted)' }}>
              <th
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-text-secondary)',
                  fontWeight: 600,
                  width: '160px',
                }}
              >
                필드
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-danger)',
                  fontWeight: 600,
                  width: '50%',
                }}
              >
                Before (-)
              </th>
              <th
                style={{
                  textAlign: 'left',
                  padding: 'var(--spacing-2) var(--spacing-3)',
                  borderBottom: '1px solid var(--color-surface-border)',
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--color-success)',
                  fontWeight: 600,
                }}
              >
                After (+)
              </th>
            </tr>
          </thead>
          <tbody>
            {diff.map((entry, i) => {
              const isAdded = entry.before === null && entry.after !== null;
              const isRemoved = entry.before !== null && entry.after === null;

              return (
                <tr
                  key={i}
                  style={{
                    background:
                      isAdded
                        ? 'color-mix(in srgb, var(--color-success) 6%, transparent)'
                        : isRemoved
                          ? 'color-mix(in srgb, var(--color-danger) 6%, transparent)'
                          : 'color-mix(in srgb, var(--color-warning) 6%, transparent)',
                  }}
                >
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-primary)',
                      fontWeight: 600,
                      verticalAlign: 'top',
                    }}
                  >
                    {entry.path}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: isRemoved || (!isAdded)
                        ? 'var(--color-danger)'
                        : 'var(--color-text-muted)',
                      verticalAlign: 'top',
                      wordBreak: 'break-all',
                    }}
                  >
                    {entry.before !== null ? (
                      <span>- {formatValue(entry.before)}</span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>(없음)</span>
                    )}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: isAdded || (!isRemoved)
                        ? 'var(--color-success)'
                        : 'var(--color-text-muted)',
                      verticalAlign: 'top',
                      wordBreak: 'break-all',
                    }}
                  >
                    {entry.after !== null ? (
                      <span>+ {formatValue(entry.after)}</span>
                    ) : (
                      <span style={{ color: 'var(--color-text-muted)' }}>(없음)</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : (
        <p
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-text-muted)',
            fontStyle: 'italic',
          }}
        >
          변경 내역이 없습니다
        </p>
      )}

      {/* Before / After 전체 JSON */}
      {(before !== null || after !== null) && (
        <div style={{ marginTop: 'var(--spacing-4)' }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--spacing-3)',
            }}
          >
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  color: 'var(--color-danger)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                Before
              </div>
              <pre
                aria-label="변경 전 JSON"
                style={{
                  margin: 0,
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderLeft: '3px solid var(--color-danger)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--color-text-primary)',
                  overflow: 'auto',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {before !== null
                  ? JSON.stringify(before, null, 2)
                  : '(없음)'}
              </pre>
            </div>
            <div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 600,
                  color: 'var(--color-success)',
                  marginBottom: 'var(--spacing-1)',
                }}
              >
                After
              </div>
              <pre
                aria-label="변경 후 JSON"
                style={{
                  margin: 0,
                  padding: 'var(--spacing-3)',
                  background: 'var(--color-surface-elevated)',
                  border: '1px solid var(--color-surface-border)',
                  borderLeft: '3px solid var(--color-success)',
                  borderRadius: 'var(--radius-sm)',
                  fontSize: 'var(--font-size-xs)',
                  fontFamily: 'var(--font-family-mono)',
                  color: 'var(--color-text-primary)',
                  overflow: 'auto',
                  maxHeight: '200px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                }}
              >
                {after !== null
                  ? JSON.stringify(after, null, 2)
                  : '(없음)'}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
